import { format } from "date-fns";
import { API_TBase } from "./API";
import { TCase_Dependant_Applicant } from "@/models/cases";
import { DEPENDANT_INSIDE_SERVICE_TYPE } from "@/constants/services";
import { convertBadgeType } from "@/models/candidate";

const DATETIME_FORMAT = "dd/MM/yyyy";

export type API_TCase = {
  case_type: string;
  case_template: TCase_Template;
  case_progress_step: TCase_Progress_Step;
  submission_date: string;
  CreatedAt: string;
  status: string;
  main_applicant: TCase_Main_Applicant;
  owner: TCase_Owner;
  progress: number;
  final_decision: string;
  dependant_applicant?: TCase_Dependant_Applicant;
} & API_TBase;

export type TCase_Template = {
  name: string;
  name_dev: string;
  with_dependant: boolean;
  case_type?: string;
  case_layout_tabs: TCase_Layout_Tab[];
};

export type TCase_Layout_Tab = {
  index: number;
  tab_type_id: number;
};

export type TCase_Progress_Step = {
  name: string;
  task_template: TCase_Task_Template;
};

export type TCase_Task_Template = {
  priority: string;
};

export type TCase_Main_Applicant = {
  first_name: string;
  last_name: string;
  job_title?: string;
  work_site?: string;
};

export type TCase_Owner = {
  first_name: string;
  last_name: string;
};

export type TCase_Table = {
  id: number;
  name: string;
  type: string;
  applicant_name: string;
  jobPosition?: string;
  owner?: string;
  submissionDate: string;
  createdAt: string;
  case_progress_step_name: string;
  progress: number;
  status: string;
  final_decision: string;
  workSite: string;
  case_type: string;
  tabId: number;
  step: string;
  caseFor: string;
  finalDecision: string;
  category: string;
  inactive: boolean;
  badgeType: string;
};

export type TTableFilter = {
  label: string;
  value: boolean;
};

const replaceUnderscoreBySpace = (s: string) => s.replace("_", " ");

const firstLetterToUpperCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const convertStatus = (status: string) => {
  status = replaceUnderscoreBySpace(status);
  status = firstLetterToUpperCase(status);

  return status;
};

const convertDatetime = (datetime: string) => {
  const formatStr = DATETIME_FORMAT;

  const value: any = datetime && Date.parse(datetime) > 0 ? new Date(datetime) : null;

  return value ? format(value, formatStr) : "―";
};

const itemIsOnFinalDecision = (a: API_TCase) => {
  const progress = Number(a.progress ?? 0);

  return progress === 100 || a.final_decision !== "―";
};

const getCaseStatus = (a: API_TCase) => {
  if (itemIsOnFinalDecision(a)) return a.final_decision ? a.final_decision : "―";

  const status = a.status ? convertStatus(a.status) : "―";

  return `${status} - ${a.progress}%`;
};

const getApplicantName = (a: API_TCase): string => {
  const dependantCase = [DEPENDANT_INSIDE_SERVICE_TYPE].includes(a.case_template.case_type);

  if (dependantCase) {
    const { first_name, last_name } = a.dependant_applicant;

    if (first_name && last_name) return `${first_name} ${last_name}`;
  } else {
    const { first_name, last_name } = a.main_applicant;

    if (first_name && last_name) return `${first_name} ${last_name}`;
  }

  return "―";
};

export const convertCasesForTable = (a: API_TCase): TCase_Table => {
  return {
    id: a.ID,
    name: a.case_template.name ? a.case_template.name : "―",
    type: a.case_template.with_dependant ? "Main applicant +\xa0dependant" : "Main applicant",
    applicant_name: getApplicantName(a),
    jobPosition: a.main_applicant?.job_title ? a.main_applicant?.job_title : "―",
    owner:
      a.owner.first_name && a.owner.last_name ? `${a.owner.first_name} ${a.owner.last_name}` : "―",
    submissionDate: convertDatetime(a.submission_date),
    createdAt: convertDatetime(a.CreatedAt),
    case_progress_step_name: a.case_progress_step.name ?? "―",
    progress: a.progress,
    status: getCaseStatus(a),
    final_decision: getCaseStatus(a),
    // final_decision: a.final_decision ? convertStatus(a.final_decision) : "―",
    workSite: a.main_applicant.work_site ? a.main_applicant.work_site : "―",
    case_type: a.case_template.name ? a.case_template.name : "―",
    tabId: a.case_template.case_layout_tabs.find((tab) => tab.index === 0).tab_type_id || 0,
    step: a.case_progress_step.name ?? "―",
    caseFor:
      a.main_applicant.first_name && a.main_applicant.last_name
        ? `${a.main_applicant.first_name} ${a.main_applicant.last_name}`
        : "―",
    finalDecision: a.final_decision ? convertStatus(a.final_decision) : "―",
    category: a.case_template.name ? a.case_template.name : "―",
    inactive: false,
    badgeType: "",
  };
};

export type TTable_Services_Employee = {
  id: number;
  name: string;
  applicant_name: string;
  case_progress_step_name: string;
  final_decision: string;
  progress: number;
  inactive: boolean;
  badgeType: string;
};

const convertFinalDecision = (final_decision: string, completed: boolean) => {
  if (!final_decision) return "―";

  if (!completed) return final_decision;

  if (final_decision == "Low risk, eligible") return "Completed, Low risk";
  if (final_decision == "Medium risk, eligible") return "Completed, Medium risk";
  if (final_decision == "High risk, not eligible") return "Completed, High risk";

  if (final_decision == "Approved") return "Granted";
  if (final_decision == "Rejected") return "Rejected";

  return final_decision;
};

export const convertServicesForTable = (a: API_TCase): TTable_Services_Employee => {
  return {
    id: a.ID,
    name: a.case_template.name ? a.case_template.name : "―",
    applicant_name: getApplicantName(a),
    case_progress_step_name: a.case_progress_step.name ?? "―",
    final_decision: convertFinalDecision(a.final_decision, a.progress == 100),
    progress: a.progress,
    inactive: a.progress == 100,
    badgeType: convertBadgeType(convertStatus(a.final_decision), a.progress == 100),
  };
};
